import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { buildGraph, type ObjectMap } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import { GLTFLoader, type GLTF } from 'three-stdlib'
import { preload as preloadSuspenseEntry, suspend } from 'suspend-react'
import { fileAsset, gltfAsset, gltfAssetUrls } from './assetStore'
import {
    assertCompleteGlb,
    decodeAssetJson,
    loadAssetWithRetry,
} from './assetFetcher'
import {
    createDeferred,
    createPreloadQueue,
    type Deferred,
    type PreloadJob,
} from './preloadQueue'
import {
    parseTrackPathJson,
    type TrackPath,
    type TrackPathJson,
} from './models/track/trackImporter'
import { applyTrackMeshEndDistance } from './models/track/trackMeshBuilder'
import { preloadSounds } from '../utils/soundPlayer'

export { fileAsset, gltfAsset, textureAsset } from './assetStore'
export { hdriAsset } from './assetStore'

const MAX_PARALLEL_DOWNLOADS = 4
const TRACK_PATH_SUSPENSE_KEY = 'preload/track-path'

type PreloadedGltf = GLTF & ObjectMap

const gltfDeferreds = new Map<string, Deferred<[PreloadedGltf]>>(
    gltfAssetUrls.map((url) => [url, createDeferred<[PreloadedGltf]>()]),
)
const trackPathDeferred = createDeferred<TrackPath>()

const threeDAssetPreload = createPreloadQueue({
    name: 'preloadAssets',
    total: gltfAssetUrls.length + 1,
    concurrency: MAX_PARALLEL_DOWNLOADS,
})

function resourceDirectory(url: string) {
    return url.slice(0, url.lastIndexOf('/') + 1)
}

function createGltfJob(url: string, deferred: Deferred<[PreloadedGltf]>): PreloadJob {
    return {
        label: url,
        run: async () => {
            const gltf = await loadAssetWithRetry(url, (bytes) => {
                assertCompleteGlb(url, bytes)
                return new GLTFLoader().parseAsync(bytes.slice(0), resourceDirectory(url))
            })
            deferred.resolve([Object.assign(gltf, buildGraph(gltf.scene))])
        },
    }
}

function createTrackPathJob(): PreloadJob {
    const url = fileAsset.Track_Path_Json

    return {
        label: url,
        run: async () => {
            const trackPath = await loadAssetWithRetry(url, (bytes) => {
                const json = decodeAssetJson<TrackPathJson>(bytes)
                if (!Array.isArray(json?.segments)) {
                    throw new Error(`${url} does not describe a track path`)
                }
                return parseTrackPathJson(json)
            })
            trackPathDeferred.resolve(trackPath)
        },
    }
}

export function startThreeDAssetPreload() {
    const jobs: PreloadJob[] = [createTrackPathJob()]

    for (const [url, deferred] of gltfDeferreds) {
        preloadSuspenseEntry(() => deferred.promise, [GLTFLoader, url])
        jobs.push(createGltfJob(url, deferred))
    }

    preloadSounds()
    threeDAssetPreload.start(jobs)
}

export function retryFailedThreeDAssets() {
    threeDAssetPreload.retryFailed()
}

export function useThreeDAssetPreload(enabled = true) {
    const snapshot = useSyncExternalStore(
        threeDAssetPreload.subscribe,
        threeDAssetPreload.getSnapshot,
        threeDAssetPreload.getSnapshot,
    )

    useEffect(() => {
        if (!enabled) return
        startThreeDAssetPreload()
    }, [enabled])

    return snapshot
}

export function useTrackPathPreloaded() {
    const trackPath = suspend(
        () => trackPathDeferred.promise,
        [TRACK_PATH_SUSPENSE_KEY],
    )
    const { scene: trackScene } = useGLTF(gltfAsset.Track)

    return useMemo(
        () => applyTrackMeshEndDistance(trackPath, trackScene),
        [trackPath, trackScene],
    )
}
